import { Box, Icons, useTheme } from '@ublend-npm/aulaui-core';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { goToSubmissions } from '@app/actions/navigation';
import { getCurrentSpaceId } from '@core/selectors/space';
import { institutionShortName } from '@core/selectors/institution';
import { AulaState } from '@core/types/state';
import { OVERVIEW_PAGE, QUIZZES_PROVIDER } from '@core/assignments/constants';
import { createStyles } from './AssignmentDetails.styles';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import { emptyAssignmentDetailsText } from '../../../../../../app/constants/texts';
import { ResponsiveAssignment } from '../types';
import AssignmentHeader from '../AssignmentHeader/AssignmentHeader';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import QuizDecommissionBanner from '../SubmissionsResponsive/QuizDecommissionBanner';
import {
  UWS_INSTITUTIONS,
  COVENTRY_INSTITUTIONS,
  NO_STUDENT_MAIN_COHORT,
} from '../constants';
import InfoAlert from '../SubmissionsResponsive/InfoAlert';

interface AssignmentDetailsProps {
  assignments: ResponsiveAssignment[];
  currentUserRole: 'educator' | 'staff' | 'student';
  showEmptyCohortAlert?: boolean;
  selectedAssignment?: ResponsiveAssignment | null;
  onMaterialListClick: (assignmentId: string) => void;
}

const AssignmentDetails = ({
  assignments,
  currentUserRole = 'student',
  showEmptyCohortAlert = false,
  selectedAssignment,
  onMaterialListClick,
}: AssignmentDetailsProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const dispatch = useDispatch();
  const spaceId = useSelector((state: AulaState) => getCurrentSpaceId(state));
  const institution = useSelector(institutionShortName);
  const accessingAsStaff =
    useSelector(accessingCurrentSpaceAsStaff) || currentUserRole === 'staff';
  const assignmentId = selectedAssignment?.id || selectedAssignment?.objectId;

  const isQuizAssignment = selectedAssignment?.provider === QUIZZES_PROVIDER;
  const quizBannerInstitution =
    institution && UWS_INSTITUTIONS.includes(institution)
      ? 'uws'
      : institution && COVENTRY_INSTITUTIONS.includes(institution)
      ? 'coventry'
      : undefined;

  // Reuses the legacy submissions route:
  // /dashboard/:spaceId/journey/assignments/:assignmentId/submissions
  const handleViewSubmissions = () => {
    if (!spaceId || !assignmentId) {
      return;
    }
    dispatch(goToSubmissions({ spaceId, assignmentId }));
  };

  if (assignments?.length === 0 || !selectedAssignment) {
    return (
      <Box sx={styles.emptyWrapper}>
        <NoResultsCard
          title={emptyAssignmentDetailsText[currentUserRole].title}
          description={emptyAssignmentDetailsText[currentUserRole].description}
          sx={styles.noResultsCard}
          titleSx={styles.noResultsCardTitle}
          descriptionSx={styles.noResultsCardDescription}
          icon={<Icons.FolderOffOutlined sx={{ fontSize: 64 }} aria-hidden />}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.wrapper}>
      {isQuizAssignment && (
        <Box sx={styles.contentBox}>
          <QuizDecommissionBanner institution={quizBannerInstitution} />
        </Box>
      )}
      <AssignmentHeader
        assignment={selectedAssignment}
        page={OVERVIEW_PAGE}
        handleViewSubmissions={handleViewSubmissions}
        isEducator={currentUserRole === 'educator'}
        isStudent={currentUserRole === 'student'}
        accessingAsStaff={accessingAsStaff}
        currentSpaceId={spaceId}
        onMaterialListClick={onMaterialListClick}
      />
      {showEmptyCohortAlert && (
        <Box sx={styles.contentBox}>
          <InfoAlert title={NO_STUDENT_MAIN_COHORT.TITLE} sx={{ mt: '1.5rem' }}>
            {NO_STUDENT_MAIN_COHORT.MESSAGE}
          </InfoAlert>
        </Box>
      )}
    </Box>
  );
};

export default AssignmentDetails;
